import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

interface Review {
  id: string;
  rating: number;
  review_text: string;
  created_at: string;
  reviewer: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

export default function ListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [listing, setListing] = useState<any>(null);
  const [hostProfile, setHostProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [showDiscountTerms, setShowDiscountTerms] = useState(false);
  const [months, setMonths] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingRequested, setBookingRequested] = useState(false);
  const [isAlreadyBooked, setIsAlreadyBooked] = useState(false);
  const [contactingHost, setContactingHost] = useState(false);
  const [isFavourited, setIsFavourited] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  const formatHostName = (fullName: string | null | undefined): string => {
    if (!fullName || !fullName.trim()) return 'Member';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
  };

  const formatJoinedDate = (createdAt: string | null | undefined): string => {
    if (!createdAt) return 'Joined recently';
    const date = new Date(createdAt);
    const month = date.toLocaleString('en-AU', { month: 'short' });
    const year = date.getFullYear();
    return `Joined ${month} ${year}`;
  };

  useEffect(() => {
    fetchListing();
    if (id) fetchReviews();
  }, [id]);

  useEffect(() => {
    const checkFavourite = async () => {
      if (!user?.id || !listing?.id) return;
      const { data } = await supabase.from('favourites').select('id').eq('user_id', user.id).eq('listing_id', listing.id).maybeSingle();
      setIsFavourited(!!data);
    };
    checkFavourite();
  }, [user?.id, listing?.id]);

  const toggleFavourite = async () => {
    if (!user?.id) { navigate('/login'); return; }
    if (isFavourited) {
      await supabase.from('favourites').delete().eq('user_id', user.id).eq('listing_id', listing.id);
      setIsFavourited(false);
    } else {
      await supabase.from('favourites').insert({ user_id: user.id, listing_id: listing.id });
      setIsFavourited(true);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: listing?.title, url }); } catch (err) {}
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const { data: reviewsData, error } = await supabase.from('reviews').select('id, rating, review_text, created_at, reviewer_id').eq('listing_id', id).order('created_at', { ascending: false });
      if (error) throw error;
      if (reviewsData && reviewsData.length > 0) {
        const reviewerIds = [...new Set(reviewsData.map(r => r.reviewer_id))];
        const { data: profilesData } = await supabase.from('profiles').select('id, full_name, avatar_url').in('id', reviewerIds);
        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const reviewsWithProfiles: Review[] = reviewsData.map(review => ({
          id: review.id, rating: review.rating, review_text: review.review_text, created_at: review.created_at,
          reviewer: profilesMap.get(review.reviewer_id) || null
        }));
        setReviews(reviewsWithProfiles);
        setTotalReviews(reviewsData.length);
        setAverageRating(Math.round((reviewsData.reduce((sum, r) => sum + r.rating, 0) / reviewsData.length) * 10) / 10);
      } else {
        setReviews([]); setTotalReviews(0); setAverageRating(0);
      }
    } catch (error) {
    } finally { setReviewsLoading(false); }
  };

  const fetchListing = async () => {
    try {
      const { data, error } = await supabase.from('listings').select('*').eq('id', id).single();
      if (error) throw error;
      setListing(data);
      const today = new Date().toISOString().split('T')[0];
      const { data: activeBookings } = await supabase.from('bookings').select('id').eq('listing_id', id).in('status', ['active', 'pending', 'confirmed']).gte('end_date', today).limit(1);
      setIsAlreadyBooked(!!(activeBookings && activeBookings.length > 0));
      if (data?.host_id) {
        const { data: profileData } = await supabase.from('profiles').select('full_name, created_at').eq('id', data.host_id).maybeSingle();
        setHostProfile(profileData);
      }
    } catch (error) {
    } finally { setLoading(false); }
  };

  const handleContactHost = async () => {
    if (!user) { navigate('/login'); return; }
    if (listing?.host_id === user.id) { alert('This is your own listing!'); return; }
    try {
      setContactingHost(true);
      const { data: existingConvo } = await supabase.from('conversations').select('id').or(`and(participant1_id.eq.${user.id},participant2_id.eq.${listing.host_id}),and(participant1_id.eq.${listing.host_id},participant2_id.eq.${user.id})`).maybeSingle();
      if (existingConvo) { navigate(`/messages/${existingConvo.id}`); return; }
      const { data: newConvo, error } = await supabase.from('conversations').insert({ participant1_id: user.id, participant2_id: listing.host_id, listing_id: listing.id }).select().single();
      if (error) throw error;
      navigate(`/messages/${newConvo.id}`);
    } catch (error) {
      alert('Failed to start conversation. Please try again.');
    } finally { setContactingHost(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><Header />
        <div className="flex items-center justify-center h-96"><div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading listing...</p>
        </div></div><Footer /></div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-white"><Header />
        <div className="flex items-center justify-center h-96"><div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-600"></i>
          <p className="mt-4 text-gray-600">Listing not found</p>
        </div></div><Footer /></div>
    );
  }

  const images = (listing?.images && Array.isArray(listing.images) && listing.images.length > 0)
    ? listing.images
    : ['https://readdy.ai/api/search-image?query=modern%20clean%20storage%20space%20interior%20professional%20photography%20bright%20lighting&width=800&height=600&seq=detail-fallback&orientation=landscape'];

  const features = Array.isArray(listing?.features) && listing.features.length > 0
    ? listing.features.map((f: string) => ({ icon: 'ri-checkbox-circle-fill', text: f }))
    : [];

  const pricingUnit = listing.pricing_unit || listing.pricingUnit || 'month';
  const monthlyPrice = listing.price_per_month || listing.price || 0;
  const hasFirstMonthDiscount = listing.first_month_discount === true && months >= 2;
  const discountPercent = listing.discount_percent || 50;
  const firstMonthPrice = hasFirstMonthDiscount ? monthlyPrice * (1 - discountPercent / 100) : monthlyPrice;
  const remainingMonthsPrice = months > 1 ? monthlyPrice * (months - 1) : 0;
  const subtotal = firstMonthPrice + remainingMonthsPrice;
  const firstMonthSubtotal = firstMonthPrice;
  const serviceFee = firstMonthSubtotal * 0.1;
  const total = firstMonthSubtotal + serviceFee;
  const discountAmount = hasFirstMonthDiscount ? monthlyPrice * (discountPercent / 100) : 0;
  const totalBeforeDiscount = monthlyPrice * months;

  const calculateEndDate = (start: string, monthsCount: number) => {
    if (!start) return '';
    const startDateObj = new Date(start);
    const endDateObj = new Date(startDateObj);
    endDateObj.setMonth(endDateObj.getMonth() + monthsCount);
    return endDateObj.toISOString().split('T')[0];
  };

  const endDate = calculateEndDate(startDate, months);

  const handleBooking = async () => {
    if (!user) { navigate('/login'); return; }
    if (!profile?.is_verified) {
      const goToVerify = confirm('You need to verify your identity before booking. Go to Settings?');
      if (goToVerify) navigate('/settings');
      return;
    }
    if (!startDate) { alert('Please select a start date'); return; }
    setBookingLoading(true);
    try {
      const calculatedEndDate = calculateEndDate(startDate, months);
      const expiresAt = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
      const monthlyRate = listing.price_per_month;
      const firstMonthAmount = listing.first_month_discount ? monthlyRate * 0.5 : monthlyRate;
      const remainingAmount = months > 1 ? monthlyRate * (months - 1) : 0;
      const bookingSubtotal = firstMonthAmount + remainingAmount;
      const serviceFee = bookingSubtotal * 0.1;
      const hostFee = bookingSubtotal * 0.1;
      const hostEarnings = bookingSubtotal - hostFee;
      const totalAmount = bookingSubtotal + serviceFee;
      const { data: booking, error: bookingError } = await supabase.from('bookings').insert({
        listing_id: listing.id, renter_id: user.id, host_id: listing.host_id,
        start_date: startDate, end_date: calculatedEndDate, months,
        monthly_rate: monthlyRate, total_amount: totalAmount, service_fee: serviceFee,
        platform_fee: hostFee, host_earnings: hostEarnings,
        status: 'pending_host_approval', payment_status: 'unpaid', expires_at: expiresAt,
      }).select().single();
      if (bookingError) { alert('Failed to submit request: ' + JSON.stringify(bookingError)); setBookingLoading(false); return; }
      const renterName = profile?.full_name || user.email || 'A renter';
      const startFormatted = new Date(startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
      const endFormatted = new Date(calculatedEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
      const messageText = `New Booking Request!\n\n${renterName} has requested to rent "${listing.title}".\n\nStart: ${startFormatted}\nEnd: ${endFormatted}\nDuration: ${months} month${months > 1 ? 's' : ''}\nMonthly rate: $${monthlyRate}/month\n\nPlease accept or decline in your Bookings dashboard within 48 hours.\n\nBooking ID: ${booking.id}`;
      const { data: existingConvo } = await supabase.from('conversations').select('id').or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`).or(`participant1_id.eq.${listing.host_id},participant2_id.eq.${listing.host_id}`).maybeSingle();
      let conversationId = existingConvo?.id;
      if (!conversationId) {
        const { data: newConvo } = await supabase.from('conversations').insert({ participant1_id: user.id, participant2_id: listing.host_id }).select('id').single();
        conversationId = newConvo?.id;
      }
      if (conversationId) {
        await supabase.from('messages').insert({ conversation_id: conversationId, sender_id: user.id, content: messageText, read: false });
      }
      try {
        const hostProfileData = await supabase.from('profiles').select('email').eq('id', listing.host_id).single();
        if (hostProfileData.data?.email) {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-request-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hostEmail: hostProfileData.data.email, renterName, listingTitle: listing.title, startDate: startFormatted, endDate: endFormatted, months, monthlyRate, bookingId: booking.id })
          });
        }
      } catch (emailErr) { console.warn('Host notification email failed:', emailErr); }
      setBookingRequested(true);
      setBookingLoading(false);
    } catch (err) {
      alert('An error occurred. Please try again.');
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        <div className="flex items-center text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-blue-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-2"></i>
          <Link to="/browse" className="hover:text-blue-600 cursor-pointer">Browse</Link>
          <i className="ri-arrow-right-s-line mx-2"></i>
          <span className="text-gray-900">{listing.title}</span>
        </div>

        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{listing.title}</h1>
            <div className="flex items-center gap-4 text-gray-600">
              <div className="flex items-center">
                {totalReviews > 0 ? (
                  <><i className="ri-star-fill text-yellow-400 mr-1"></i><span className="font-semibold text-gray-900">{averageRating}</span><span className="ml-1">({totalReviews} review{totalReviews !== 1 ? 's' : ''})</span></>
                ) : (<span className="text-gray-500">No reviews yet</span>)}
              </div>
              <div className="flex items-center"><i className="ri-map-pin-line mr-1"></i><span>{listing.location}</span></div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleShare} className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"><i className="ri-share-line text-xl"></i></button>
            <button onClick={toggleFavourite} className={`p-3 border rounded-lg cursor-pointer transition-colors ${isFavourited ? 'bg-red-50 border-red-300 text-red-500' : 'border-gray-300 hover:bg-gray-50'}`}>
              <i className={`${isFavourited ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`}></i>
            </button>
          </div>
        </div>

        <div className="mb-3 rounded-2xl overflow-hidden">
          <img src={images[selectedImage]} alt="Main view" className="w-full object-contain max-h-[600px]" />
        </div>

        {images.length > 1 && (
          <div className="flex gap-2 mb-12 overflow-x-auto pb-2">
            {images.map((img, index) => (
              <img key={index} src={img} alt={`View ${index + 1}`} onClick={() => setSelectedImage(index)}
                className={`flex-shrink-0 w-24 h-16 object-cover rounded-lg cursor-pointer transition-all ${selectedImage === index ? 'ring-2 ring-blue-600 opacity-100' : 'opacity-60 hover:opacity-100'}`} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between pb-8 border-b border-gray-200 mb-8">
              <div className="flex items-center">
                <img src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20friendly%20Australian%20host%20in%20their%2030s%2C%20smiling%20warmly%20with%20natural%20lighting%2C%20clean%20background%2C%20professional%20portrait%20photography&width=100&height=100&seq=host-avatar&orientation=squarish" alt="Host" className="w-16 h-16 rounded-full object-cover object-top mr-4" />
                <div>
                  <h3 className="font-bold text-gray-900">Hosted by {formatHostName(hostProfile?.full_name)}</h3>
                  <p className="text-sm text-gray-600">{formatJoinedDate(hostProfile?.created_at)}</p>
                </div>
              </div>
              <button onClick={handleContactHost} disabled={contactingHost} className="px-6 py-2.5 border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                {contactingHost ? (<><i className="ri-loader-4-line animate-spin mr-2"></i>Connecting...</>) : ('Contact Host')}
              </button>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About this space</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{listing.description || 'Spacious and secure garage located in the heart of Bondi. Perfect for storing vehicles, boats, equipment, or general storage needs.'}</p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Features & Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3"><i className={`${feature.icon} text-[#14B8A6] text-xl`}></i></div>
                    <span className="text-gray-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              {reviewsLoading ? (
                <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line text-2xl text-gray-400 animate-spin"></i></div>
              ) : totalReviews > 0 ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6"><i className="ri-star-fill text-yellow-400 mr-2"></i>{averageRating} · {totalReviews} review{totalReviews !== 1 ? 's' : ''}</h2>
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0">
                        <div className="flex items-start mb-3">
                          {review.reviewer?.avatar_url ? (
                            <img src={review.reviewer.avatar_url} alt={review.reviewer?.full_name || 'Reviewer'} className="w-12 h-12 rounded-full object-cover object-top mr-4" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4"><i className="ri-user-line text-gray-500 text-xl"></i></div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-bold text-gray-900">{review.reviewer?.full_name || 'Anonymous'}</h4>
                              <span className="text-sm text-gray-500">{formatTimeAgo(review.created_at)}</span>
                            </div>
                            <div className="flex items-center mb-2">
                              {[...Array(5)].map((_, i) => (<i key={i} className={`ri-star-fill text-sm ${i < review.rating ? 'text-yellow-400' : 'text-gray-200'}`}></i>))}
                            </div>
                            {review.review_text && (<p className="text-gray-700 leading-relaxed">{review.review_text}</p>)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalReviews > 3 && (<button className="mt-6 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">Show all {totalReviews} reviews</button>)}
                </>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-chat-smile-3-line text-gray-400 text-2xl"></i></div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
                  <p className="text-gray-600">Be the first to review this space after your booking!</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-24">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold text-gray-900">${monthlyPrice}</span>
                <span className="text-gray-600">per month</span>
              </div>
              <div className="flex items-center gap-2 mb-6">
                {totalReviews > 0 ? (
                  <><span className="text-yellow-400">★</span><span className="font-semibold">{averageRating}</span><span className="text-gray-600">({totalReviews} review{totalReviews !== 1 ? 's' : ''})</span></>
                ) : (<span className="text-gray-500 text-sm">No reviews yet</span>)}
              </div>

              {listing.first_month_discount && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2"><i className="ri-price-tag-3-fill text-green-600"></i><span className="text-green-800 font-semibold">50% off first month</span></div>
                  <button onClick={() => setShowDiscountTerms(true)} className="text-green-600 hover:text-green-700 text-sm underline whitespace-nowrap">Terms</button>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2"><label className="text-sm font-semibold text-gray-900">Number of Months: {months}</label></div>
                <input type="range" min="1" max="24" value={months} onChange={(e) => setMonths(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                <div className="flex justify-between text-xs text-gray-500 mt-1"><span>1 month</span><span>24 months</span></div>
              </div>

              {startDate && (
                <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">End Date</div>
                  <div className="text-base font-semibold text-gray-900 mt-1">{new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                </div>
              )}

              <button onClick={handleBooking} disabled={!startDate || bookingLoading || isAlreadyBooked || bookingRequested}
                className={`w-full text-white font-semibold py-4 rounded-lg transition-colors mb-4 whitespace-nowrap disabled:cursor-not-allowed ${bookingRequested ? 'bg-green-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300'}`}>
                {bookingLoading ? 'Submitting Request...' : bookingRequested ? '✓ Booking Requested' : isAlreadyBooked ? 'Currently Unavailable' : 'Request to Book'}
              </button>

              <p className="text-center text-sm text-gray-600 mb-6">{bookingRequested ? '✓ Request sent! The host has 48 hours to respond.' : "You won't be charged yet — the host must accept first."}</p>

              <div className="space-y-3 pt-6 border-t border-gray-200">
                <div className="flex justify-between text-sm font-medium text-blue-700 bg-blue-50 rounded-lg px-3 py-2"><span>Due on Approval (Month 1 only)</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Month 1 rent</span><span className="font-medium">${monthlyPrice.toFixed(2)}</span></div>
                {hasFirstMonthDiscount && (
                  <div className="flex justify-between text-sm text-green-600"><span>First month discount ({discountPercent}%)</span><span className="font-medium">-${discountAmount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-sm"><span className="text-gray-600">Service fee</span><span className="font-medium">${serviceFee.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-bold pt-3 border-t border-gray-200"><span>Due on Approval</span><span>${total.toFixed(2)}</span></div>
                {months > 1 && (
                  <div className="flex justify-between text-sm text-gray-500 pt-2 border-t border-gray-100">
                    <span>Then ${monthlyPrice.toFixed(2)}/month × {months - 1} month{months - 1 !== 1 ? 's' : ''}</span><span>billed monthly</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {showDiscountTerms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button onClick={() => setShowDiscountTerms(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"><i className="ri-close-line text-xl"></i></button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full"><i className="ri-price-tag-3-fill text-green-600 text-xl"></i></div>
              <h3 className="text-xl font-bold text-gray-900">First Month Discount Terms</h3>
            </div>
            <div className="space-y-4 text-gray-700">
              <div className="flex gap-3"><i className="ri-checkbox-circle-fill text-green-600 mt-1 flex-shrink-0"></i><p className="text-sm"><strong>{discountPercent}% discount</strong> applies to the first month only</p></div>
              <div className="flex gap-3"><i className="ri-checkbox-circle-fill text-green-600 mt-1 flex-shrink-0"></i><p className="text-sm">Subsequent months are charged at the regular monthly rate of <strong>${monthlyPrice}</strong></p></div>
              <div className="flex gap-3"><i className="ri-checkbox-circle-fill text-green-600 mt-1 flex-shrink-0"></i><p className="text-sm">Discount is automatically applied at checkout</p></div>
              <div className="flex gap-3"><i className="ri-checkbox-circle-fill text-green-600 mt-1 flex-shrink-0"></i><p className="text-sm">Valid for new bookings of 2 months or more</p></div>
            </div>
            <button onClick={() => setShowDiscountTerms(false)} className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors whitespace-nowrap">Got it</button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
